import {
  isChartValid,
  isDashletValid,
} from 'components/DashboardGrid/validation';
import { ButtonGroupImageButton } from 'components/Inputs/ButtonGroup/Button';
import Icon from 'components/Kizen/Icon';
import { Icon as KdsIcon } from '@kizen/kds/Icon';
import BasicModal from 'components/Modals/presets/BasicModal';
import useModal from 'components/Modals/useModal';
import DashletWizard from 'components/Wizards/Dashlet';
import RTDVWizard from 'components/Wizards/RTDV';
import useWizardInModal from 'components/Wizards/shared/hooks/useWizardInModal';
import { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BlockTypeModal,
  ButtonGrid,
  CompoundIcon,
  IntegrationsSection,
  PluginButtonGrid,
  SectionLabel,
} from './styles';
import { PluginIcon } from 'ts-components/Plugins/PluginIcon';
import { HomepageBuilder } from 'ts-components/StaticBlockBuilder/HomepageBuilder';
import { useModalClickedState } from 'components/DashboardGrid/hooks/useModalClickedState';
import {
  MODES,
  MODE_TO_BLOCK_SURFACE,
} from 'components/DashboardGrid/hooks/useDashboardChildren';
import { useSelector } from 'react-redux';
import { getBusinessClientObject } from 'store/authentication/selectors';
import { PluginContext } from 'ts-components/Plugins/context';
import PluginBlockWizard from './PluginBlockWizard';
import { AI_BLOCK_TYPE } from 'ts-components/AiBlockPanel/aiBlockSession';
import { useKiCodeAccess } from 'ts-components/KiCode/useKiCodeAccess';

const hasAccess = true;

// AI_BLOCK_TYPE opens the page's AI sidebar
export const ALLOWED_BLOCK_TYPES_BY_MODE = {
  [MODES.HOMEPAGES]: ['dashlet', 'chart', 'static', AI_BLOCK_TYPE],
  [MODES.DASHBOARD]: ['dashlet', 'static', AI_BLOCK_TYPE],
  [MODES.CHARTS]: ['chart', 'static', AI_BLOCK_TYPE],
};

const BlockModal = (props) => {
  const {
    existing = {},
    onConfirm,
    currentDashboard,
    mode = MODES.HOMEPAGES,
    // Forwarded to the chart (RTDV) wizard when used on chart groups, where the
    // object is fixed rather than chosen inside the wizard.
    objectId,
    contacts,
    model,
    ...rest
  } = props;

  // On homepages the wizards run in "homepage" mode (object is selectable);
  // on dashboards and chart groups they run scoped to the surface's object.
  const isHomepage = mode === MODES.HOMEPAGES;
  const [chosenOption, setChosenOption] = useState(null);
  const [modalSize, setModalSize] = useState('medium');
  const [builderOpen, setBuilderOpen] = useState(false);
  const [builderData, setBuilderData] = useState(null);
  const [selectedPlugin, setSelectedPlugin] = useState(null);

  const { t } = useTranslation();
  const canAccessKiCode = useKiCodeAccess();

  const { modalProps, wizardProps } = useWizardInModal(
    (chart) => onConfirm(chart, builderData),
    // The initial modal is always valid
    (state) => {
      if (chosenOption === 'dashlet') {
        return isDashletValid(state);
      }
      if (chosenOption === 'chart') {
        return isChartValid(state);
      }
      return false;
    },
    existing
  );

  const isEditing = Object.keys(existing).length > 0;

  const { businessPluginAppsByApiName, pluginBlocks } =
    useContext(PluginContext);
  const surface =
    MODE_TO_BLOCK_SURFACE[mode] ?? MODE_TO_BLOCK_SURFACE[MODES.HOMEPAGES];

  // Only show plugins that expose at least one block on this surface.
  const activePluginEntries = useMemo(() => {
    return Object.entries(businessPluginAppsByApiName)
      .map(([apiName, bizPlugin]) => {
        const blockEntry = pluginBlocks.find(
          (p) => p.plugin_api_name === apiName
        );
        const blocks = (blockEntry?.blocks ?? []).filter(
          (block) => !block.types?.length || block.types.includes(surface)
        );
        return {
          plugin_api_name: apiName,
          plugin_name: bizPlugin.plugin_app.name,
          thumbnail: bizPlugin.plugin_app.thumbnail,
          blocks,
        };
      })
      .filter((entry) => entry.blocks.length > 0);
  }, [businessPluginAppsByApiName, pluginBlocks, surface]);

  const options = useMemo(() => {
    const allowedTypes =
      ALLOWED_BLOCK_TYPES_BY_MODE[mode] ??
      ALLOWED_BLOCK_TYPES_BY_MODE[MODES.HOMEPAGES];
    return [
      {
        value: 'dashlet',
        label: t('Dashlet (Time Series)'),
        image: <Icon icon="chart-line" />,
      },
      {
        value: 'chart',
        label: t('Chart (Object Data)'),
        image: <Icon icon="chart-pie-light" />,
      },
      {
        value: 'static',
        label: t('Static Content (Custom Builder)'),
        image: (
          <CompoundIcon>
            <Icon icon="text" />
            <Icon icon="image-landscape" />
          </CompoundIcon>
        ),
      },
      {
        value: AI_BLOCK_TYPE,
        label: t('Build with AI'),
        image: (
          <div>
            <KdsIcon icon="text-ai" size="xl" color="background-dark" />
          </div>
        ),
      },
    ].filter(
      (option) =>
        allowedTypes.includes(option.value) &&
        (canAccessKiCode || option.value !== AI_BLOCK_TYPE)
    );
  }, [t, mode, canAccessKiCode]);

  const clientObject = useSelector(getBusinessClientObject);

  const [addDashletModalProps, addDashletModalTriggerProps] = useModal();
  const [pluginWizardModalProps, pluginWizardModalTriggerProps] = useModal();

  const secondaryModalHeading = (() => {
    const noun = chosenOption === 'dashlet' ? t('Dashlet') : t('Chart');
    return isEditing
      ? t('Edit {{noun}}', { noun })
      : t('Add New {{noun}}', { noun });
  })();

  const { clicked, handleClickConfirm } = useModalClickedState(
    modalProps?.onConfirm
  );

  const isPluginWizardOpen = pluginWizardModalProps.show;

  return (
    <>
      {/* This is the wizard for building the block,
	  determined by which option they chose in the first modal (below) */}
      {builderOpen && (
        <HomepageBuilder
          onCancel={() => {
            setBuilderOpen(false);
            setChosenOption(null);
            props.onHide();
          }}
          onSave={(data) => {
            setBuilderData(data);
            onConfirm(undefined, data);
          }}
          onSaveAndClose={(data) => {
            setBuilderOpen(false);
            onConfirm(undefined, data);
            props.onHide();
          }}
          defaultSettings={currentDashboard?.defaultStyleSettings}
          modalLayer={1}
        />
      )}

      {isPluginWizardOpen && selectedPlugin && (
        <PluginBlockWizard
          plugin={selectedPlugin}
          mode={mode}
          onHide={() => {
            pluginWizardModalProps.onHide();
            setSelectedPlugin(null);
            props.onHide();
          }}
          onConfirm={(blockData) => {
            pluginWizardModalProps.onHide();
            onConfirm({ type: 'plugin_block', ...blockData }, undefined);
            props.onHide();
          }}
        />
      )}

      <BasicModal
        defaultLeftBtnText={hasAccess ? t('Cancel') : undefined}
        leftBtn={hasAccess ? undefined : false}
        buttonText={hasAccess ? (isEditing ? t('Save') : t('Add')) : t('Close')}
        actionBtnColor={hasAccess ? 'green' : 'blue'}
        className="no-drag"
        fitContent={hasAccess ? false : 'y'}
        heading={secondaryModalHeading}
        size={hasAccess ? modalSize : undefined}
        disabled={(hasAccess ? modalProps.disabled : false) || clicked}
        typeOfContent={hasAccess ? modalProps.typeOfContent : 'text'}
        {...addDashletModalProps}
        onConfirm={hasAccess ? handleClickConfirm : undefined}
        onHide={rest.onHide}
        animateSizeChange
        enforceFocus={false}
        autoFocus={false}
        restoreFocus={false}
      >
        {chosenOption === 'dashlet' ? (
          <DashletWizard
            homepage={isHomepage}
            existing={existing}
            isEditing={isEditing}
            setModalSize={setModalSize}
            currentDashboard={currentDashboard}
            {...wizardProps}
          />
        ) : null}
        {chosenOption === 'chart' ? (
          <RTDVWizard
            homepage={isHomepage}
            existing={existing}
            isEditing={isEditing}
            setModalSize={setModalSize}
            modalSize={modalSize}
            clientObjectId={clientObject.id}
            objectId={objectId}
            contacts={contacts}
            model={model}
            currentDashboard={currentDashboard}
            {...wizardProps}
          />
        ) : null}
      </BasicModal>
      {/* This is the default modal shown to the user, to select
	  from the 3 types of blocks they can add. Once a type is chosen we
	  unmount it so the wizard/builder is the only dialog in the DOM —
	  otherwise two stacked modals confuse modal-scoped queries. */}
      {isEditing ||
      builderOpen ||
      addDashletModalProps.show ||
      isPluginWizardOpen ? null : (
        <BlockTypeModal
          actionBtnColor={'blue'}
          className="no-drag"
          heading={t('Add New Block')}
          buttonText={t('Cancel')}
          leftBtn={false}
          size="medium"
          {...modalProps}
          {...rest}
          disabled={false}
          modalBodyProps={{ typeOfContent: 'no-paddings' }}
          onConfirm={rest.onHide}
        >
          <ButtonGrid count={options.length}>
            {options.map((option) => (
              <ButtonGroupImageButton
                key={option.value}
                selected={chosenOption === option.value}
                option={option}
                data-qa-id={`block-type-${option.value}`}
                onClick={() => {
                  setChosenOption(option.value);
                  if (option.value === 'static') {
                    setBuilderOpen(true);
                  } else if (option.value === AI_BLOCK_TYPE) {
                    // The page opens its AI sidebar; nothing to build here, and
                    // nothing stays selected for the next time the picker opens
                    setChosenOption(null);
                    onConfirm({ type: AI_BLOCK_TYPE }, undefined);
                    props.onHide();
                  } else {
                    addDashletModalTriggerProps.onClick();
                  }
                }}
              />
            ))}
          </ButtonGrid>

          {activePluginEntries.length > 0 && (
            <IntegrationsSection>
              <SectionLabel as="h3" type="subheader">
                {t('Integrations')}
              </SectionLabel>
              <PluginButtonGrid>
                {activePluginEntries.map((pluginEntry) => (
                  <ButtonGroupImageButton
                    key={pluginEntry.plugin_api_name}
                    selected={
                      selectedPlugin?.plugin_api_name ===
                      pluginEntry.plugin_api_name
                    }
                    option={{
                      label: pluginEntry.plugin_name,
                      image: <PluginIcon thumbnail={pluginEntry.thumbnail} height={30} marginBottom={0} />,
                    }}
                    data-qa-id={`block-type-plugin-${pluginEntry.plugin_api_name}`}
                    onClick={() => {
                      setSelectedPlugin(pluginEntry);
                      pluginWizardModalTriggerProps.onClick();
                    }}
                  />
                ))}
              </PluginButtonGrid>
            </IntegrationsSection>
          )}
        </BlockTypeModal>
      )}
    </>
  );
};

export default BlockModal;
